import { DevTool } from "@hookform/devtools";
import { FC, PropsWithChildren, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilValue } from "recoil";
import styled from "styled-components";

import { useCrudEditAdminContext, useYupValidationResolver } from "@ryde/redux";
import {
  DATA_E2E_KEY,
  IExtendableDto,
  IStyledAttributes,
  KeyboardKeys,
  Log,
  shouldDebugForms,
  shouldResetOnAddNew,
  shouldResetOnEditOrDuplicate,
  yupValidationSchemaSF,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import {
  getCustomFieldValidationSchema,
  getFormSchemaValidationSchema,
  getGridSchemaValidationSchema,
} from "./validation-schemas";

const FormTagStyled = styled.form.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "form-tag",
})`
  background-color: ${rydeColors.white};
`;

export const EditFormTagWrapperAdmin: FC<PropsWithChildren> = ({
  children,
}) => {
  const {
    data: { dto, isEdit, isDuplicate },
    entity,
  } = useCrudEditAdminContext<IExtendableDto>();

  const yupSchemaDynamicRecoil = useRecoilValue(yupValidationSchemaSF(entity));
  const validationSchema = useMemo(() => {
    if (entity === "customField") {
      return getCustomFieldValidationSchema(isDuplicate);
    }

    if (entity === "formSchema") {
      return getFormSchemaValidationSchema(isDuplicate);
    }

    if (entity === "gridSchema") {
      return getGridSchemaValidationSchema(isDuplicate);
    }

    return yupSchemaDynamicRecoil;
  }, [entity, isDuplicate, yupSchemaDynamicRecoil]);
  const yupDynamicResolver = useYupValidationResolver(validationSchema, entity);

  const methods = useForm<IExtendableDto>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    resolver: yupDynamicResolver,
  });

  const {
    reset,
    clearErrors,
    control,
    setValue,
    trigger,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    if (!isDirty) {
      if (shouldResetOnEditOrDuplicate({ dto, isEdit, isDuplicate })) {
        Log.debug(`form reset called for entity ${entity}, dto:`, dto);
        clearErrors();
        reset({ ...dto });

        if (isEdit) {
          /* Display an error if db value is not synchronized
           with updated validation rules (RYDE-3913) */
          trigger();
        }
      } else if (dto && shouldResetOnAddNew({ dto, isEdit, isDuplicate })) {
        clearErrors();
        Log.debug(
          `Setting initial values called for entity ${entity}, dto:`,
          dto,
        );
        Object.entries(dto).forEach(([field, value]) => {
          if (value !== undefined && value !== null) {
            setValue(field, value, { shouldDirty: true });
          }
        });
      }
    }
  }, [
    clearErrors,
    dto,
    entity,
    isDirty,
    isDuplicate,
    isEdit,
    reset,
    setValue,
    trigger,
  ]);

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key !== KeyboardKeys.ENTER) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target.tagName === "TEXTAREA" || target.isContentEditable) {
      return;
    }
    event.preventDefault();
  };

  return (
    <FormProvider {...methods}>
      <FormTagStyled
        noValidate
        onSubmit={handleFormSubmit}
        onKeyDown={handleKeyDown}
        data-e2e-entity={entity}
      >
        {children}
      </FormTagStyled>
      {shouldDebugForms() && <DevTool control={control} />}
    </FormProvider>
  );
};
